import type { PlayerState, StructureState, WeaponKey, TurnAction } from '../types';
import { structureJob, standingWithJob } from './body';
import {
  WEAPONS,
  ECOTOURISM_INCOME,
  ECOTOURISM_DESTROY_FINE,
  MAX_ECOTOURISM_PER_PLAYER,
  DELIVERY_COST_MULTIPLIER,
  JUMPPACK_FUEL_COST,
} from '../constants';

/**
 * Get the CBP cost of an action.
 */
export function getActionCost(action: TurnAction, weaponKey?: WeaponKey): number {
  switch (action) {
    case 'fire':
      return weaponKey ? WEAPONS[weaponKey].cbpCost : 0;
    case 'jump':
      return 1;
    case 'jetpack':
      return 4;
    case 'build':
      return 3; // base cost, could vary by structure type
    case 'shop':
      return 0;
    case 'skip':
      return 0;
    default:
      return 0;
  }
}

/**
 * Check if a player can afford an action.
 */
export function canAfford(player: PlayerState, action: TurnAction, weaponKey?: WeaponKey): boolean {
  return player.cbp >= getActionCost(action, weaponKey);
}

/**
 * Deduct CBP for an action. Returns the new CBP value.
 */
export function spendCBP(player: PlayerState, action: TurnAction, weaponKey?: WeaponKey): number {
  const cost = getActionCost(action, weaponKey);
  return Math.max(0, player.cbp - cost);
}

/**
 * Calculate income for a player at the start of their turn.
 * Returns CBP to add.
 */
export function calculateIncome(playerId: number, structures: StructureState[]): number {
  let income = 0;
  for (const s of structures) {
    // X18: whatever the table says pays its owner (the ecotourism centre today)
    if (s.ownerId === playerId && s.health > 0) income += structureJob(s).income ?? 0;
  }
  return income;
}

/**
 * Apply the ICC fine when an ecotourism center is destroyed.
 * Returns the new CBP value.
 */
export function applyDestructionFine(player: PlayerState): number {
  return Math.max(0, player.cbp - ECOTOURISM_DESTROY_FINE);
}

/**
 * Check if a player can build another ecotourism center.
 */
export function canBuildEcotourism(playerId: number, structures: StructureState[]): boolean {
  const count = structures.filter(s => s.ownerId === playerId && standingWithJob(s, 'income')).length;
  return count < MAX_ECOTOURISM_PER_PLAYER;
}

/**
 * Get a summary of a player's economy for display.
 */
export function getEconomySummary(
  player: PlayerState,
  structures: StructureState[]
): { cbp: number; incomePerTurn: number; ecotourismCount: number } {
  const income = calculateIncome(player.id, structures);
  const ecoCount = structures.filter(s => s.ownerId === player.id && standingWithJob(s, 'income')).length;
  return { cbp: player.cbp, incomePerTurn: income, ecotourismCount: ecoCount };
}

// =============================================================================
// IN-GAME SHOP (DRONE DELIVERY)
// =============================================================================

/**
 * Get the delivery cost for a weapon (3x standard CBP cost).
 */
export function getDeliveryCost(weaponKey: WeaponKey): number {
  return WEAPONS[weaponKey].cbpCost * DELIVERY_COST_MULTIPLIER;
}

/**
 * Calculate total cost for a set of shop purchases at delivery prices.
 */
export function calculateShopTotal(purchases: Partial<Record<WeaponKey, number>>): number {
  let total = 0;
  for (const key of Object.keys(purchases) as WeaponKey[]) {
    const qty = purchases[key] || 0;
    total += getDeliveryCost(key) * qty;
  }
  return total;
}

/**
 * Check if a player can afford a set of shop purchases.
 */
export function canAffordShop(player: PlayerState, purchases: Partial<Record<WeaponKey, number>>): boolean {
  return player.cbp >= calculateShopTotal(purchases);
}

/**
 * Apply shop purchases: deduct CBP and add ammo. Returns new PlayerState (immutable).
 */
export function applyShopPurchases(
  player: PlayerState,
  purchases: Partial<Record<WeaponKey, number>>
): PlayerState {
  const total = calculateShopTotal(purchases);
  const newAmmo = { ...player.loadout.ammo };

  for (const key of Object.keys(purchases) as WeaponKey[]) {
    const qty = purchases[key] || 0;
    if (qty <= 0) continue;
    const current = newAmmo[key];
    // null means unlimited — don't change
    if (current === null || current === undefined) continue;
    newAmmo[key] = current + qty;
  }

  return {
    ...player,
    cbp: Math.max(0, player.cbp - total),
    loadout: {
      ...player.loadout,
      ammo: newAmmo,
    },
  };
}

/**
 * Get the delivery cost for jumppack fuel (3x standard cost).
 */
export function getJumppackFuelDeliveryCost(): number {
  return JUMPPACK_FUEL_COST * DELIVERY_COST_MULTIPLIER;
}

/**
 * Calculate total cost for shop purchases plus fuel at delivery prices.
 */
export function calculateShopTotalWithFuel(
  purchases: Partial<Record<WeaponKey, number>>,
  fuelQty: number
): number {
  return calculateShopTotal(purchases) + fuelQty * getJumppackFuelDeliveryCost();
}

/**
 * Apply shop purchases and fuel: deduct CBP, add ammo, add fuel. Returns new PlayerState.
 */
export function applyShopPurchasesWithFuel(
  player: PlayerState,
  purchases: Partial<Record<WeaponKey, number>>,
  fuelQty: number
): PlayerState {
  const updated = applyShopPurchases(player, purchases);
  const fuelCost = fuelQty * getJumppackFuelDeliveryCost();
  return {
    ...updated,
    cbp: Math.max(0, updated.cbp - fuelCost),
    jumppackFuel: updated.jumppackFuel + fuelQty,
  };
}
